import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { MobileBottomBar } from "@/components/MobileBottomBar";
import { FinalCtaSection } from "@/components/FinalCtaSection";
import { EliteClientRoster } from "@/components/EliteClientRoster";

const GeoUSA = () => {
  useEffect(() => {
    document.title = "Hire Technical VA in USA | Marjohn Robillo";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Hire an elite Technical Virtual Assistant from the Philippines for your US-based agency. Seamless EST/PST timezone overlap and direct hiring without agency markups.",
      );
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement("meta");
      metaKeywords.setAttribute("name", "keywords");
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute(
      "content",
      "hire technical VA USA, technical virtual assistant Philippines, CRM automation expert US, RevOps specialist USA, GoHighLevel expert US, n8n developer USA, offshore technical VA, Philippines VA for US agency",
    );

    const ogTags: Record<string, string> = {
      "og:title": "Hire Technical VA in USA | Marjohn Robillo",
      "og:description":
        "Hire an elite Technical Virtual Assistant from the Philippines for your US-based agency. Seamless EST/PST timezone overlap and direct hiring without agency markups.",
      "og:type": "website",
      "og:url": "https://marjohnrobillo.me/hire-technical-va-usa",
      "og:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
      "twitter:card": "summary_large_image",
      "twitter:title": "Hire Technical VA in USA | Marjohn Robillo",
      "twitter:description":
        "Hire an elite Technical VA from the Philippines for your US-based agency.",
      "twitter:image":
        "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
    };
    Object.entries(ogTags).forEach(([name, content]) => {
      let element =
        document.querySelector(`meta[property="${name}"]`) ||
        document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(
          name.startsWith("og:") ? "property" : "name",
          name,
        );
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    });

    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute(
      "href",
      "https://marjohnrobillo.me/hire-technical-va-usa",
    );
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Technical Virtual Assistant & RevOps Architect for US Agencies",
    description:
      "Hire an elite Technical Virtual Assistant from the Philippines for your US-based agency. Seamless EST/PST timezone overlap and direct hiring without agency markups.",
    url: "https://marjohnrobillo.me/hire-technical-va-usa",
    areaServed: ["US", "CA"],
    address: { "@type": "PostalAddress", addressCountry: "PH" },
    priceRange: "$$",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Can a Philippines-based technical VA work US business hours?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. The Philippines operates on a timezone that overlaps with EST, CST, and PST business hours, enabling real-time Slack communication and same-day technical execution.",
        },
      },
      {
        "@type": "Question",
        name: "How much does a technical VA cost compared to a US hire?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A senior technical VA from the Philippines typically costs $1,500-$4,000/month compared to $8,000-$15,000 for equivalent US-based talent, saving 60-75% without compromising quality.",
        },
      },
    ],
  };

  const features = [
    {
      title: "US Timezone Alignment",
      desc: "Available during your core business hours (EST/PST). Real-time Slack communication and same-day technical execution.",
    },
    {
      title: "No Agency Markups",
      desc: "Direct partnership means your budget goes 100% toward elite technical engineering, not middleman fees.",
    },
    {
      title: "US Compliance",
      desc: "W-8BEN compliant, secure password management, and strict NDA protocols for US-based businesses.",
    },
  ];

  return (
    <main
      className="min-h-screen relative pb-[72px] md:pb-0"
      style={{ background: "#FFFFFF" }}
    >
      <MobileBottomBar />
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <section className="relative pt-36 md:pt-44 pb-24 px-6 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 72% 30%, rgba(11,124,242,0.10) 0%, transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-6 right-0 select-none hidden md:block"
        >
          <span
            className="font-display font-black leading-none"
            style={{
              fontSize: "clamp(160px, 22vw, 320px)",
              color: "rgba(17,143,247,0.07)",
              letterSpacing: "-0.07em",
            }}
          >
            USA
          </span>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2.5 mb-8 justify-center">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ background: "#27DC5D" }}
            />
            <span
              className="font-sans text-xs font-semibold uppercase tracking-[0.22em]"
              style={{ color: "var(--primary)" }}
            >
              🇺🇸 Supporting US-Based SaaS &amp; Agencies
            </span>
          </div>

          <h1
            className="font-display font-extrabold mb-7 leading-[0.95]"
            style={{
              fontSize: "clamp(40px, 5.6vw, 76px)",
              letterSpacing: "-0.045em",
              color: "#071E61",
            }}
          >
            Hire a Technical VA &amp; CRM Automation Expert from the Philippines
            for <span style={{ color: "var(--primary)" }}>US Agencies</span>
          </h1>

          <p
            className="mx-auto mb-10"
            style={{
              fontSize: "clamp(17px, 1.5vw, 20px)",
              lineHeight: 1.55,
              color: "#536B89",
              maxWidth: "640px",
            }}
          >
            Stop paying 80% agency markups. Partner directly with a
            Philippine-based RevOps Architect who works seamlessly in EST, CST,
            and PST timezones.
          </p>
        </div>
      </section>

      <EliteClientRoster />

      {/* Feature cards */}
      <section className="relative py-20 px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="relative rounded-[24px] p-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBFF 100%)",
                border: "1px solid rgba(8,46,103,0.07)",
                boxShadow: "0 14px 40px rgba(8,46,103,0.08)",
              }}
            >
              <div
                className="font-display font-black mb-5 select-none"
                style={{
                  fontSize: "56px",
                  lineHeight: 1,
                  color: "rgba(17,143,247,0.08)",
                  letterSpacing: "-0.04em",
                }}
              >
                0{i + 1}
              </div>
              <h3
                className="font-display font-bold mb-3"
                style={{
                  fontSize: "22px",
                  color: "#071E61",
                  letterSpacing: "-0.03em",
                }}
              >
                {f.title}
              </h3>
              <p
                style={{ fontSize: "15px", lineHeight: 1.55, color: "#536B89" }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <FinalCtaSection />
      <Footer />
    </main>
  );
};

export default GeoUSA;
